/**
 * useProductsByCategory - Fetch products by category ID
 *
 * Uses standard Magento products query with category filter.
 * Returns transformed Product[] for ProductCard components.
 */
import { useQuery } from '@tanstack/react-query'

import { homepageApi, transformMagentoProductsToProducts } from '../../lib'
import { homepageKeys } from '../queryKeys'

import type { Product, ProductsData } from '../../types'

export interface UseProductsByCategoryOptions {
  /** Category ID to fetch products from */
  categoryId: string
  /** Number of products to fetch */
  pageSize: number
  /** Enable/disable the query */
  enabled?: boolean
}

export interface UseProductsByCategoryResult {
  /** Transformed products for ProductCard */
  products: Product[]
  /** Total count of products in category */
  totalCount: number
  /** Loading state */
  isLoading: boolean
  /** Error state */
  isError: boolean
  /** Error object if any */
  error: Error | null
}

/**
 * Fetch products by category ID using standard Magento products query
 */
export const useProductsByCategory = ({
  categoryId,
  pageSize,
  enabled = true,
}: UseProductsByCategoryOptions): UseProductsByCategoryResult => {
  const { data, isLoading, isError, error } = useQuery<ProductsData>({
    queryKey: homepageKeys.productsByCategory(categoryId, pageSize),
    queryFn: () => homepageApi.getProductsByCategory(categoryId, pageSize),
    enabled: enabled && !!categoryId,
  })

  const magentoProducts = data?.products?.items ?? []
  const products = transformMagentoProductsToProducts(magentoProducts)
  const totalCount = data?.products?.total_count ?? 0

  return {
    products,
    totalCount,
    isLoading,
    isError,
    error: error ?? null,
  }
}
